import { useQueryClient } from '@tanstack/react-query'
import { type VFC } from 'react'
import { StaticRouter } from 'react-router-dom/server'
import {
  optimisticallyUpdateStoryForAccess,
  updateStoryForPersistedAccess,
  useReadStoryAccess,
  useUpdateStoryAccess,
} from '@opallabs/api'
import { type SimpleWorkspace } from '@opallabs/concepts'
import { type Theme } from '@opallabs/gem'
import { DefaultQueryClientProvider } from '@opallabs/higgs'
import { ThemeProvider, WorkspaceContext } from '@opallabs/hooks'
import { ensureOwnerIsInAccess } from '../helpers'
import { ResourceShareModal } from '../ResourceShareModal'

type StoryShareModalProps = Readonly<{
  legacyStoryId: string
  storyTitle: string
  storyUuid: string
  open: boolean
  onClose: () => void
  ownerUuid?: string
  readOnly?: boolean
}>

const StoryShareModal: VFC<StoryShareModalProps> = ({
  legacyStoryId,
  storyTitle,
  storyUuid,
  onClose,
  open,
  ownerUuid,
  readOnly = false,
}) => {
  const queryClient = useQueryClient()
  const unmodifiedAccess = useReadStoryAccess({
    storyUuid,
    enabled: open,
    // Refetch on open so the modal reflects latest permissions
    refetchOnMount: 'always',
  })
  const currentAccess = ensureOwnerIsInAccess(unmodifiedAccess, ownerUuid)

  const updateStoryAccess = useUpdateStoryAccess({
    onMutate: (mutationParams) => {
      optimisticallyUpdateStoryForAccess(queryClient, mutationParams)
    },
    onSuccess: (data) => {
      updateStoryForPersistedAccess(queryClient, data)
    },
  })

  return (
    <ResourceShareModal
      effectivePrivacy={null}
      isVisible={open}
      onClose={onClose}
      metadataQuery={currentAccess}
      onChangeAccess={async (access) => {
        await updateStoryAccess.mutateAsync({ access, id: storyUuid })
      }}
      ownerId={ownerUuid}
      readOnly={readOnly}
      resourceId={storyUuid}
      resourceShareParams={{
        legacyStoryId: String(legacyStoryId),
      }}
      resourceTitle={storyTitle}
      resourceType="story"
    />
  )
}

export const QueryableStoryShareModal: VFC<
  StoryShareModalProps & { workspace: SimpleWorkspace; theme?: Theme }
> = ({ workspace, theme = 'light', ...props }) => {
  const location = window.top?.location ?? window.location
  return (
    <DefaultQueryClientProvider>
      <StaticRouter location={location.pathname}>
        <WorkspaceContext.Provider value={workspace}>
          <ThemeProvider theme={theme}>
            <StoryShareModal {...props} />
          </ThemeProvider>
        </WorkspaceContext.Provider>
      </StaticRouter>
    </DefaultQueryClientProvider>
  )
}
